import APP_CONFIG from '@/config/app';
import camelcaseKeys from 'camelcase-keys';
import { format } from 'date-fns';
export const formatNumberToAccountingString = (
  value: number,
  round: boolean = false,
): string => {
  if (isNaN(value)) {
    return '0.00';
  }
  const formatWithPrecision = (num: number): string => {
    const absNum = Math.abs(num);
    if (absNum === 0) return '0.00';
    if (absNum < 0.000001) {
      return absNum.toExponential(2).replace('+', '');
    }
    if (absNum < 1) {
      const decimalPlaces = Math.min(6, -Math.floor(Math.log10(absNum)) + 1);
      return absNum.toFixed(decimalPlaces).replace(/\.?0+$/, '');
    }
    return round ? absNum.toFixed(0) : absNum.toFixed(2);
  };
  const formatWithSuffix = (num: number, suffix: string): string => {
    return `${formatWithPrecision(num)}${suffix}`;
  };
  if (value >= 1e12 || value <= -1e12) {
    return formatWithSuffix(value / 1e12, 'T');
  } else if (value >= 1e9 || value <= -1e9) {
    return formatWithSuffix(value / 1e9, 'B');
  } else if (value >= 1e6 || value <= -1e6) {
    return formatWithSuffix(value / 1e6, 'M');
  } else if (value >= 1e3 || value <= -1e3) {
    return formatWithSuffix(value / 1e3, 'K');
  } else {
    const formattedValue = formatWithPrecision(value);
    return value < 0 ? `(${formattedValue})` : formattedValue;
  }
};
export const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  const absValue = Math.abs(value);
  const formattedValue = `${absValue.toFixed(2)}%`;
  return value < 0 ? `(${formattedValue})` : formattedValue;
};
export const formatCompositionPercentage = (value: number): string => {
  const percentage = value * 100;
  if (percentage < 0.01 && percentage > 0) {
    return '< 0.01';
  }
  return percentage.toFixed(2);
};
export const formatNumberToAmountInUsdString = (
  value: number,
  round: boolean = false,
): string => {
  if (isNaN(value)) {
    return '$0.00';
  }
  let formattedValue: string;
  if (round) {
    value = Math.round(value);
  }
  formattedValue = `$${formatNumberToAccountingString(value, round)}`;
  return value < 0 ? `(${formattedValue})` : formattedValue;
};
export const formatBalanceToBalanceInUsdString = (
  aggregatedBalanceInUsd: number,
): string => {
  return `Balance ${formatNumberToAmountInUsdString(aggregatedBalanceInUsd)}`;
};
export const formatNumberToAmountInTokenCurrencyString = (
  value: number,
  tokenSymbol: string,
): string => {
  if (isNaN(value)) {
    return `0.00 ${tokenSymbol}`;
  }
  const formattedValue = `${formatNumberToAccountingString(
    Math.abs(value),
  )} ${tokenSymbol}`;
  return value < 0 ? `(${formattedValue})` : formattedValue;
};
export const formatBalanceToBalanceInTokenCurrencyString = (
  balanceInTokenCurrency: number,
  tokenSymbol: string,
): string => {
  return `Balance ${formatNumberToAmountInTokenCurrencyString(
    balanceInTokenCurrency,
    tokenSymbol,
  )}`;
};
export function formatBalancesToBalanceString(
  amountInTokenCurrency: number,
  amountInUsd: number,
  tokenSymbol: string,
  showBalancePrefix: boolean = true,
): string {
  const tokenBalanceString = formatNumberToAmountInTokenCurrencyString(
    amountInTokenCurrency,
    tokenSymbol,
  );
  const usdBalanceString = formatNumberToAmountInUsdString(amountInUsd);
  const prefix = showBalancePrefix ? 'Balance ' : '';
  return `${prefix}${tokenBalanceString} (${usdBalanceString})`;
}
export const formatNumberToAPYString = (value: number): string => {
  return `${value.toFixed(2)}% APY`;
};
export const formatNumberToAPRString = (value: number): string => {
  return `${value.toFixed(2)}% APR`;
};
export const formatRiskOrReturnScoreToDescription = (score: number): string => {
  switch (score) {
    case 0:
      return 'Very Low';
    case 1:
      return 'Low';
    case 2:
      return 'Moderate';
    case 3:
      return 'High';
    case 4:
      return 'Very High';
    default:
      return 'Unknown';
  }
};
export const formatToSentenceCase = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};
export const formatToTitleCase = (text: string): string => {
  return text
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};
export const formatDate = (
  dateInput: string | Date,
  formatStr: string = 'MMM d, yyyy, h:mm a zzz',
): string => {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  return format(date, formatStr);
};
export const formatStringToLast6Characters = (str: string): string => {
  return str.slice(-6);
};
export const formatStringToFirstAndLast4Characters = (str: string): string => {
  if (str.length <= 8) {
    return str;
  }
  const firstFour = str.slice(0, 4);
  const lastFour = str.slice(-4);
  return `${firstFour}...${lastFour}`;
};
export function convertKeysToCamelCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeysToCamelCase(item));
  } else if (obj !== null && obj.constructor === Object) {
    return camelcaseKeys(obj, { deep: true });
  }
  return obj;
}
export function formatYieldDataForChart(
  data: Array<any>,
  metricKey: string,
  monthsToShow: number = APP_CONFIG.HISTORICAL_YIELD_DATA_TO_SHOW_IN_MONTHS,
): Array<{ time: string; value: number; label: string }> {
  const currentDate = new Date();
  const monthsInMilliseconds = monthsToShow * 30 * 24 * 60 * 60 * 1000;
  const filteredData =
    monthsToShow === 0
      ? data
      : data.filter((item) => {
          const dataDate = new Date(item.timestamp);
          const timeDiff = currentDate.getTime() - dataDate.getTime();
          return timeDiff <= monthsInMilliseconds;
        });
  const dataToUse = filteredData.length < 2 ? data : filteredData;
  return dataToUse.map((item) => {
    const time: string = item.timestamp ? item.timestamp.split('T')[0] : '';
    return {
      time,
      value: item[metricKey],
      label: '',
    };
  });
}
export function formatNumberToUpToTenDecimals(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 10,
    useGrouping: false,
  });
}
export const formatStringItemsToCsvInASentence = (
  items: string[],
  conjunction: 'and' | 'or' = 'and',
): string => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;
  const allButLast = items.slice(0, -1).join(', ');
  const lastItem = items[items.length - 1];
  return `${allButLast}, ${conjunction} ${lastItem}`;
};
export const formatDefiLlamaProjectNames = (str: string): string => {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};
export const roundToSixDecimals = (value: string | number): number => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) {
    throw new Error('Invalid number input');
  }
  return Math.floor(num * 1e6) / 1e6;
};
export const sanitizeAssetAmount = (value: string | number): number => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) {
    return 0;
  }
  const decimalPlaces = num.toString().split('.')[1]?.length || 0;
  return decimalPlaces > 6 ? roundToSixDecimals(num) : num;
};
export const convertScientificToDecimalString = (
  numberString: string,
): string => {
  const num = Number(numberString);
  if (isNaN(num)) {
    throw new Error('Invalid number format');
  }
  if (!numberString.includes('e')) {
    return numberString;
  }
  const [mantissa, exponent] = num.toString().toLowerCase().split('e');
  const exp = parseInt(exponent);
  if (exp > 0) {
    const decimalIndex = mantissa.indexOf('.');
    if (decimalIndex === -1) {
      return mantissa.padEnd(exp + mantissa.length, '0');
    }
    const newDecimalIndex = decimalIndex + exp;
    let result = mantissa.replace('.', '').padEnd(newDecimalIndex, '0');
    if (newDecimalIndex < result.length) {
      result =
        result.slice(0, newDecimalIndex) + '.' + result.slice(newDecimalIndex);
    }
    return result;
  } else {
    const absExp = Math.abs(exp);
    const result = mantissa.replace('.', '');
    return '0.' + '0'.repeat(absExp - 1) + result;
  }
};
